export interface Column {
  name: string;
  type: string;
  pk?: boolean;
  fk?: string; // target table
  nullable?: boolean;
  description?: string;
}

export interface TableSchema {
  name: string;
  label: string;
  cluster: "auth" | "ao" | "societes" | "config";
  columns: Column[];
}

export interface Relation {
  from: string;
  fromCol: string;
  to: string;
  toCol: string;
  type: "1:1" | "1:N" | "N:1";
}

export const clusterColors: Record<string, { bg: string; border: string; label: string }> = {
  auth: { bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200 dark:border-blue-800", label: "Auth & Org" },
  ao: { bg: "bg-purple-50 dark:bg-purple-950/30", border: "border-purple-200 dark:border-purple-800", label: "Coeur AO" },
  societes: { bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800", label: "Societes" },
  config: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", label: "Config" },
};

export const tables: TableSchema[] = [
  // Auth & Org
  {
    name: "users",
    label: "users",
    cluster: "auth",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "email", type: "String", description: "UNIQUE" },
      { name: "password_hash", type: "Text" },
      { name: "created_at", type: "Timestamp(tz)" },
      { name: "updated_at", type: "Timestamp(tz)" },
    ],
  },
  {
    name: "user_roles",
    label: "user_roles",
    cluster: "auth",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "user_id", type: "UUID", fk: "users" },
      { name: "role", type: "app_role", description: "admin | utilisateur" },
      { name: "created_at", type: "Timestamp(tz)" },
    ],
  },
  {
    name: "collaborateurs",
    label: "collaborateurs",
    cluster: "auth",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "nom", type: "String" },
      { name: "email", type: "String", nullable: true },
      { name: "service_id", type: "UUID", fk: "services", nullable: true },
      { name: "user_id", type: "UUID", fk: "users", nullable: true },
      { name: "est_actif", type: "Boolean" },
    ],
  },
  {
    name: "services",
    label: "services",
    cluster: "auth",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "nom", type: "String" },
      { name: "description", type: "Text", nullable: true },
    ],
  },
  // Coeur AO
  {
    name: "appels_offres",
    label: "appels_offres",
    cluster: "ao",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "titre", type: "String" },
      { name: "reference", type: "String" },
      { name: "proprietaire_id", type: "UUID", fk: "users" },
      { name: "service_responsable_id", type: "UUID", fk: "services", nullable: true },
      { name: "gemini_model", type: "String" },
      { name: "prompt_template_id", type: "UUID", fk: "prompt_templates", nullable: true },
      { name: "statut", type: "String", description: "en cours | ..." },
      { name: "seuil_shortlist", type: "Integer", description: "75" },
      { name: "seuil_consider", type: "Integer", description: "60" },
    ],
  },
  {
    name: "depots",
    label: "depots",
    cluster: "ao",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "ao_id", type: "UUID", fk: "appels_offres" },
      { name: "candidat_id", type: "UUID", fk: "candidats" },
      { name: "texte_extrait", type: "Text", nullable: true },
      { name: "ocr_utilise", type: "Boolean" },
      { name: "extraction_model", type: "String", nullable: true },
    ],
  },
  {
    name: "analyses",
    label: "analyses",
    cluster: "ao",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "depot_id", type: "UUID", fk: "depots" },
      { name: "ao_id", type: "UUID", fk: "appels_offres" },
      { name: "overall_score", type: "Integer", nullable: true },
      { name: "decision", type: "AnalysisDecision", nullable: true, description: "shortlist | consider | reject" },
      { name: "rating", type: "AnalysisRating", nullable: true, description: "A | B | C" },
      { name: "analysis_mode", type: "String", nullable: true },
      { name: "model_used", type: "String", nullable: true },
    ],
  },
  {
    name: "evaluations",
    label: "evaluations",
    cluster: "ao",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "ao_id", type: "UUID", fk: "appels_offres" },
      { name: "nom", type: "String" },
      { name: "ordre", type: "Integer" },
      { name: "ponderation_globale_pct", type: "Integer" },
    ],
  },
  {
    name: "criteres_evaluation",
    label: "criteres_evaluation",
    cluster: "ao",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "ao_id", type: "UUID", fk: "appels_offres" },
      { name: "evaluation_id", type: "UUID", fk: "evaluations" },
      { name: "nom", type: "String" },
      { name: "ponderation_pct", type: "Integer" },
    ],
  },
  // Societes
  {
    name: "societes",
    label: "societes",
    cluster: "societes",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "nom", type: "String" },
      { name: "adresse", type: "String", nullable: true },
      { name: "npa", type: "String", nullable: true },
      { name: "localite", type: "String", nullable: true },
      { name: "email", type: "String", nullable: true },
      { name: "agence_placement", type: "Boolean" },
    ],
  },
  {
    name: "candidats",
    label: "candidats",
    cluster: "societes",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "nom", type: "String" },
      { name: "email", type: "String", nullable: true },
      { name: "societe_id", type: "UUID", fk: "societes" },
    ],
  },
  {
    name: "societes_files",
    label: "societes_files",
    cluster: "societes",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "societe_id", type: "UUID", fk: "societes" },
      { name: "file_path", type: "String" },
      { name: "file_name", type: "String" },
      { name: "file_type", type: "String", nullable: true },
    ],
  },
  // Config
  {
    name: "prompt_templates",
    label: "prompt_templates",
    cluster: "config",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "name", type: "String" },
      { name: "type", type: "PromptTemplateType", description: "analysis_prompt | output_format" },
      { name: "template_content", type: "Text" },
      { name: "is_default", type: "Boolean" },
      { name: "created_by", type: "UUID", fk: "users", nullable: true },
    ],
  },
  {
    name: "ao_access_permissions",
    label: "ao_access_permissions",
    cluster: "config",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "ao_id", type: "UUID", fk: "appels_offres" },
      { name: "user_id", type: "UUID", fk: "users" },
      { name: "permission_type", type: "String", description: "read | write | admin" },
      { name: "granted_by", type: "UUID", fk: "users", nullable: true },
    ],
  },
  {
    name: "ao_societe_notes",
    label: "ao_societe_notes",
    cluster: "config",
    columns: [
      { name: "id", type: "UUID", pk: true },
      { name: "ao_id", type: "UUID", fk: "appels_offres" },
      { name: "societe_id", type: "UUID", fk: "societes" },
      { name: "note", type: "Text", nullable: true },
      { name: "note_prix", type: "Numeric", nullable: true },
      { name: "note_qualite_offre", type: "Numeric", nullable: true },
      { name: "created_by", type: "UUID", fk: "users", nullable: true },
    ],
  },
];

export const relations: Relation[] = [
  { from: "user_roles", fromCol: "user_id", to: "users", toCol: "id", type: "N:1" },
  { from: "collaborateurs", fromCol: "user_id", to: "users", toCol: "id", type: "N:1" },
  { from: "collaborateurs", fromCol: "service_id", to: "services", toCol: "id", type: "N:1" },
  { from: "appels_offres", fromCol: "proprietaire_id", to: "users", toCol: "id", type: "N:1" },
  { from: "appels_offres", fromCol: "service_responsable_id", to: "services", toCol: "id", type: "N:1" },
  { from: "appels_offres", fromCol: "prompt_template_id", to: "prompt_templates", toCol: "id", type: "N:1" },
  { from: "depots", fromCol: "ao_id", to: "appels_offres", toCol: "id", type: "N:1" },
  { from: "depots", fromCol: "candidat_id", to: "candidats", toCol: "id", type: "N:1" },
  { from: "analyses", fromCol: "depot_id", to: "depots", toCol: "id", type: "N:1" },
  { from: "analyses", fromCol: "ao_id", to: "appels_offres", toCol: "id", type: "N:1" },
  { from: "evaluations", fromCol: "ao_id", to: "appels_offres", toCol: "id", type: "N:1" },
  { from: "criteres_evaluation", fromCol: "ao_id", to: "appels_offres", toCol: "id", type: "N:1" },
  { from: "criteres_evaluation", fromCol: "evaluation_id", to: "evaluations", toCol: "id", type: "N:1" },
  { from: "candidats", fromCol: "societe_id", to: "societes", toCol: "id", type: "N:1" },
  { from: "societes_files", fromCol: "societe_id", to: "societes", toCol: "id", type: "N:1" },
  { from: "prompt_templates", fromCol: "created_by", to: "users", toCol: "id", type: "N:1" },
  { from: "ao_access_permissions", fromCol: "ao_id", to: "appels_offres", toCol: "id", type: "N:1" },
  { from: "ao_access_permissions", fromCol: "user_id", to: "users", toCol: "id", type: "N:1" },
  { from: "ao_societe_notes", fromCol: "ao_id", to: "appels_offres", toCol: "id", type: "N:1" },
  { from: "ao_societe_notes", fromCol: "societe_id", to: "societes", toCol: "id", type: "N:1" },
  { from: "ao_societe_notes", fromCol: "created_by", to: "users", toCol: "id", type: "N:1" },
];
